/* assets/auto-carousel.js */
(() => {
  // Only run if user hasn't asked for reduced motion
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // Any wrapper/slider with the class "auto-carousel" will autoplay
  const ROOT_SELECTORS = [
    'slider-component.auto-carousel',
    '.auto-carousel slider-component',
    '.auto-carousel .slider',
    '.auto-carousel [data-slider]'
  ].join(',');

  // Find the scroll container inside the root
  const findScroller = (root) => {
    if (!root) return null;
    if (root.classList?.contains('slider')) return root;
    return (
      root.querySelector?.('.slider') ||
      root.querySelector?.('[data-slider]') ||
      root.querySelector?.('ul') ||
      root.querySelector?.('.grid') ||
      root
    );
  };

  // Build list of child slides (visible, direct children most of the time)
  const getSlides = (scroller) => {
    if (!scroller) return [];
    // Prefer direct children for snap points
    let kids = Array.from(scroller.children);
    // Fallback: any snap child
    if (!kids.length) kids = Array.from(scroller.querySelectorAll(':scope > *'));
    // Only visible
    return kids.filter((el) => el.offsetParent !== null);
  };

  const computePositions = (scroller, slides) => slides.map((s) => s.offsetLeft);

  const initRoot = (root) => {
    if (!root || root.__autoCarouselInit) return;

    const scroller = findScroller(root);
    if (!scroller) return;
    if (scroller.scrollWidth <= scroller.clientWidth) return; // nothing to scroll

    // Ensure scroll-snap exists (won't override your theme if already set)
    scroller.style.scrollSnapType ||= 'x mandatory';
    getSlides(scroller).forEach((s) => {
      if (!s.style.scrollSnapAlign) s.style.scrollSnapAlign = 'start';
    });

    let slides = getSlides(scroller);
    if (!slides.length) return;

    let positions = computePositions(scroller, slides);

    const recalc = () => {
      slides = getSlides(scroller);
      positions = computePositions(scroller, slides);
    };

    // Recalculate on resize and image load (cards often change width post-load)
    const ro = new ResizeObserver(recalc);
    ro.observe(scroller);
    slides.forEach((s) =>
      s.querySelectorAll('img').forEach((img) => img.addEventListener('load', recalc, { once: true }))
    );

    // Configuration via data attribute on the root wrapper (optional)
    const interval = parseInt(root.dataset.autoplayInterval || '3500', 10);

    let paused = false;
    let timer = null;

    const isAtEnd = () => scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;

    const nextSnapLeft = () => {
      const current = scroller.scrollLeft;
      // find first snap strictly ahead; wrap to 0 if none
      const idx = positions.findIndex((p) => p > current + 2);
      return idx === -1 ? 0 : positions[idx];
    };

    const step = () => {
      if (paused) return;
      const target = nextSnapLeft();
      scroller.scrollTo({ left: target, behavior: 'smooth' });
    };

    const start = () => {
      stop();
      timer = setInterval(step, interval);
    };

    const stop = () => {
      if (timer) clearInterval(timer);
      timer = null;
    };

    // Pause on hover/focus/touch; resume after user interaction
    const pause = () => (paused = true);
    const resumeSoon = (() => {
      let t;
      return () => {
        clearTimeout(t);
        t = setTimeout(() => (paused = false), 2000);
      };
    })();

    root.addEventListener('mouseenter', pause, { passive: true });
    root.addEventListener('mouseleave', resumeSoon, { passive: true });
    scroller.addEventListener('pointerdown', pause, { passive: true });
    scroller.addEventListener('pointerup', resumeSoon, { passive: true });
    scroller.addEventListener('keydown', pause); // keyboard users
    scroller.addEventListener('focusin', pause);
    scroller.addEventListener('focusout', resumeSoon);

    document.addEventListener('visibilitychange', () => {
      paused = document.hidden;
    });

    // Theme Editor support
    document.addEventListener('shopify:section:unload', (e) => {
      if (e.target.contains(root)) {
        stop();
        ro.disconnect();
      }
    });

    root.__autoCarouselInit = true;
    start();
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTORS).forEach(initRoot);
  };

  // Initial pass
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Watch for sections inserted dynamically (Theme Editor, lazy sections)
  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node.matches?.(ROOT_SELECTORS)) initRoot(node);
        node.querySelectorAll?.(ROOT_SELECTORS).forEach(initRoot);
      });
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });

  // Theme Editor specific hook
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
})();